import {
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog, 
    handleClose,
    Page,
    PageRoute,
    pn,
    ScreenType,
    Stack,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    Permission,
    DiplomaType,
    DiplomaTypeEditResource,
    DiplomaTypeViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { DiplomaTypePageHeader } from "./DiplomaTypePageHeader";
import { BasicInfo } from "./BasicInfo";

const routeName = "diplomaType";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

export const diplomaTypePageRoute: PageRoute<Params> = {
    handle: "Diploma Type",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
};

export const DiplomaTypePage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params);
    const [diplomaType, setDiplomaType] = useState<DiplomaType>({} as DiplomaType);
    const [initDiplomaType, setInitDiplomaType] = useState<DiplomaType>(diplomaType);
    const [viewResource, setViewResource] = useState<DiplomaTypeViewResource>({});
    const [editResource, setEditResource] = useState<DiplomaTypeEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [diplomaType] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);

        ApiService.diplomaTypes.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.diplomaTypes.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${diplomaTypePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        ApiService.diplomaTypes.get(id).then((response) => {
            setDiplomaType(response);
            setInitDiplomaType(response);
            resetUnsavedChanges([response]);
        });
    };

    const pageHeader = (
        <DiplomaTypePageHeader
            rootPermission={ROOT_PERMISSION}
            handle={diplomaTypePageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            diplomaType={diplomaType}
            setDiplomaType={setDiplomaType}
            initDiplomaType={initDiplomaType}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
        />
    );

    const pageBody = (
        <Stack spacing="lg">
            <BasicInfo
                crudMode={params.crudMode}
                diplomaType={diplomaType}
                setDiplomaType={setDiplomaType}
                viewResource={viewResource}
                editResource={editResource}
                validationErrors={validationErrors}
            />
        </Stack>
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
